import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Message, Modal, Space, Table, Tag, Tooltip, Typography } from "@arco-design/web-react";
import EmptyState from "../Components/EmptyState";
import { IconDelete, IconEdit } from "@arco-design/web-react/icon";

import { DeleteApi, FetchApi, SaveApi } from "../Api/AuthenticatedApi";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard, { SummaryCardGroup } from "../Layout/SummaryCard";
import useOrgFeatures from "../utils/useOrgFeatures";
import ContactAddEditDialog from "./ContactAddEditDialog";
import ResendSignupEmail from "./ResendSignupEmail";
import { suppressionMeta } from "./contactStatus";
import {
  emailResults, emailWhat, engagement, formatCustom, formatDate, formatDateTime,
  listStatus, locationStatus, signedUpVia, sourcePath,
} from "./contactHistory";

const { Text, Title } = Typography;

// The contact page: who they are, where they stand on each list, the drip
// sequences they're in, and every email sent to them with what they did with
// it (including emails that were never sent).

const Fact = ({ label, children }) => (
  <div style={{ minWidth: 150, flex: "1 1 150px" }}>
    <div style={{ fontSize: 12, color: "var(--color-text-3)" }}>{label}</div>
    <div style={{ marginTop: 2 }}>{children}</div>
  </div>
);

const Facts = ({ children }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>{children}</div>
);

// Tags that carry a hover note show it in a tooltip.
const ResultTag = ({ tag }) => {
  const t = <Tag size="small" color={tag.color} style={{ marginRight: 4 }}>{tag.label}</Tag>;
  return tag.note ? <Tooltip content={tag.note}>{t}</Tooltip> : t;
};

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const features = useOrgFeatures();

  const [contact, setContact] = useState(null);
  const [history, setHistory] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lists, setLists] = useState([]);
  const [cfDefs, setCfDefs] = useState([]);
  const [company, setCompany] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [listsChanged, setListsChanged] = useState(false);

  const base = "/api/contacts/" + contactId;

  // then runs once the contact has loaded. A contact that won't load shows its
  // own message (notFound), with nothing else to report.
  const loadContact = useCallback((then) => {
    FetchApi(getAccessTokenSilently, base,
      (data) => { setContact(data); if (then) then(); },
      () => navigate("/login"),
      () => setNotFound(true));
  }, [getAccessTokenSilently, navigate, base]);

  const loadHistory = useCallback(() => {
    FetchApi(getAccessTokenSilently, base + "/history",
      (data) => setHistory(data || { emails: [], sequences: [] }),
      () => navigate("/login"),
      () => {
        setHistory({ emails: [], sequences: [] });
        Message.error("Failed to load the contact's email history");
      });
  }, [getAccessTokenSilently, navigate, base]);

  useEffect(() => { loadContact(loadHistory); }, [loadContact, loadHistory]);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/contact-lists/", (data) => setLists(data || []), () => {}, () => {});
  }, [getAccessTokenSilently, listsChanged]);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/custom-fields/?entity_type=contact", (data) => setCfDefs(data || []), () => {}, () => {});
  }, [getAccessTokenSilently]);

  const companyId = contact && contact.company_id;
  useEffect(() => {
    if (!features.companies || !companyId) { setCompany(null); return; }
    FetchApi(getAccessTokenSilently, "/api/companies/" + companyId, (data) => setCompany(data), () => {}, () => setCompany(null));
  }, [getAccessTokenSilently, features.companies, companyId]);

  const save = (record) => {
    SaveApi(getAccessTokenSilently, base,
      () => { Message.success("Contact saved"); loadContact(); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to save contact"),
      record);
  };

  const remove = () => {
    Modal.confirm({
      title: "Delete " + (contact.name || contact.email) + "?",
      content: "This deletes the contact and their email history. It can't be undone.",
      okText: "Delete",
      okButtonProps: { status: "danger" },
      onOk: () => DeleteApi(getAccessTokenSilently, base,
        () => { Message.success("Contact deleted"); navigate("/contacts"); },
        () => navigate("/login"),
        () => Message.error("Failed to delete contact"),
        {}),
    });
  };

  if (notFound) {
    return (
      <Content>
        <SimpleBreadcrumbs elements={[{ name: "Contacts", path: "/contacts" }, { name: "Not found" }]} />
        <EmptyState description="This contact doesn't exist, or was deleted.">
          <Button type="primary" style={{ marginTop: 12 }} onClick={() => navigate("/contacts")}>Back to Contacts</Button>
        </EmptyState>
      </Content>
    );
  }
  if (!contact || !history) return <LoadingState />;

  const status = suppressionMeta(contact.suppression_reason || (contact.unsubscribe_all_date ? "unsubscribed" : ""));
  const via = signedUpVia(history.emails);
  const totals = engagement(history.emails);
  const customFields = cfDefs
    .filter((d) => !d.archived && (contact.custom_fields || {})[d.key] != null && (contact.custom_fields || {})[d.key] !== "")
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  const location = [contact.city, contact.state, contact.country].filter(Boolean).join(", ");

  const emailColumns = [
    {
      title: "When",
      render: (_, e) => <span style={{ whiteSpace: "nowrap" }}>{formatDateTime(e.send_date || e.created_date)}</span>,
    },
    {
      title: "Email",
      render: (_, e) => {
        const what = emailWhat(e);
        const path = sourcePath(e);
        return (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>{what.kind} · </Text>
            {path ? <Link to={path}>{what.source}</Link> : <Text type="secondary">{what.source}</Text>}
            {what.detail && <Text type="secondary"> — {what.detail}</Text>}
          </div>
        );
      },
    },
    {
      title: "What happened",
      render: (_, e) => emailResults(e).map((t) => <ResultTag key={t.label} tag={t} />),
    },
  ];

  const sequenceColumns = [
    {
      title: "Sequence",
      render: (_, s) => (s.sequence_name
        ? <Link to={`/sequences/${s.sequence_id}/edit`}>{s.sequence_name}</Link>
        : <Text type="secondary">a deleted sequence</Text>),
    },
    { title: "Status", render: (_, s) => <Tag size="small">{s.status || "—"}</Tag> },
    {
      title: "Progress",
      render: (_, s) => (s.steps ? `${Math.min(s.current_step, s.steps)} of ${s.steps} emails sent` : "—"),
    },
    {
      title: "Next email",
      render: (_, s) => (s.status === "active" && s.next_send_at ? formatDateTime(s.next_send_at) : "—"),
    },
    { title: "Joined", render: (_, s) => formatDate(s.enrolled_date) },
  ];

  const listColumns = [
    { title: "List", render: (_, s) => s.list_name || "—" },
    { title: "Status", render: (_, s) => <ResultTag tag={listStatus(s)} /> },
    { title: "Joined", render: (_, s) => formatDate(s.subscribe_date) },
    { title: "Left", render: (_, s) => formatDate(s.unsubscribe_date) },
  ];

  return (
    <Content>
      <SimpleBreadcrumbs elements={[{ name: "Contacts", path: "/contacts" }, { name: contact.name || contact.email }]} />

      <SummaryCardGroup>
        <SummaryCard title="Contact">
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: "1 1 240px" }}>
              <Title heading={4} style={{ margin: 0 }}>{contact.name || contact.email}</Title>
              {contact.name && <Text type="secondary">{contact.email}</Text>}
              <div style={{ marginTop: 8 }}>
                <Tag color={status.color} style={{ marginRight: 4 }}>{status.label}</Tag>
                {(contact.tags || []).map((t) => <Tag key={t.id} size="small" style={{ marginRight: 4 }}>{t.name}</Tag>)}
              </div>
            </div>
            <Space>
              <ContactAddEditDialog
                allMailListItems={lists}
                setAllMailListItems={setLists}
                isDataUpdated={listsChanged}
                contactName={contact.name}
                contactEmail={contact.email}
                contactId={contact.id}
                contactSubscriptions={contact.subscriptions || []}
                contactCompanyId={contact.company_id || ""}
                contactCustomFields={contact.custom_fields || {}}
                contactRecordTypeId={contact.record_type_id || ""}
                isOpen={editOpen}
                setIsOpen={(open) => { setEditOpen(open); if (!open) setListsChanged((v) => !v); }}
                onSave={save}
                render={() => <Button type="primary" icon={<IconEdit />} onClick={() => setEditOpen(true)}>Edit</Button>}
              />
              <Button status="danger" icon={<IconDelete />} onClick={remove}>Delete</Button>
            </Space>
          </div>

          {contact.suppression_reason === "bounced" && (
            <Alert type="error" style={{ marginTop: 12 }} content="Their address bounced, so no more email is sent to it." />
          )}
          {contact.suppression_reason === "complained" && (
            <Alert type="error" style={{ marginTop: 12 }} content="They marked your email as spam, so no more email is sent to them." />
          )}
          {totals.notSent > 0 && (
            <Alert
              type="warning"
              style={{ marginTop: 12 }}
              content={`${totals.notSent} email(s) to this contact were never sent — the send failed. See the email list below.`}
            />
          )}

          <div style={{ marginTop: 16 }}>
            <Facts>
              <Fact label="Added">{formatDate(contact.added_email_date || contact.created_date)}</Fact>
              <Fact label="Signed up through">
                {via ? (via.path ? <Link to={via.path}>{via.name}</Link> : via.name) : <Text type="secondary">Added by hand or imported</Text>}
              </Fact>
              <Fact label="Confirmed">{formatDate(contact.confirm_email_date)}</Fact>
              <Fact label="Last opened">{formatDate(contact.last_opened_date)}</Fact>
              <Fact label="Emails">
                {totals.sent} sent · {totals.opened} opened · {totals.clicked} clicked
              </Fact>
              {location && (
                <Fact label="Location">
                  {location}
                  {locationStatus(contact) && (
                    <Text type="secondary" style={{ display: "block", fontSize: 12 }}>{locationStatus(contact).text}</Text>
                  )}
                </Fact>
              )}
              {company && <Fact label="Company"><Link to={`/companies/${company.id}`}>{company.name}</Link></Fact>}
              {customFields.map((d) => (
                <Fact key={d.key} label={d.label || d.key}>{formatCustom(contact.custom_fields[d.key], d.field_type)}</Fact>
              ))}
            </Facts>
          </div>

          <ResendSignupEmail contactId={contact.id} />
        </SummaryCard>
      </SummaryCardGroup>

      <SummaryCard title="Lists">
        <Table
          size="small" border={false} pagination={false} rowKey="contact_list_id"
          columns={listColumns} data={contact.subscriptions || []}
          noDataElement={<Text type="secondary">Not on any list.</Text>}
        />
      </SummaryCard>

      {history.sequences.length > 0 && (
        <SummaryCard title="Sequences">
          <Table
            size="small" border={false} pagination={false} rowKey="sequence_id"
            columns={sequenceColumns} data={history.sequences}
          />
        </SummaryCard>
      )}

      <SummaryCard title="Emails">
        {history.more_emails && (
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Showing the newest {history.emails.length} emails.
          </Text>
        )}
        <Table
          size="small" border={false} rowKey="id"
          pagination={history.emails.length > 25 ? { pageSize: 25 } : false}
          columns={emailColumns} data={history.emails}
          noDataElement={<Text type="secondary">No emails sent to this contact yet.</Text>}
        />
      </SummaryCard>
    </Content>
  );
}
